import { z } from 'zod'

import { type HeaderItem, headerItemSchema } from '@/schema/navigation/header'
import { type Icon, iconSchema, pathSchema } from '@/schema/utility'

import {
  type InternalRootGroupNavigationEntry,
  internalRootGroupNavigationEntrySchema,
  type RootGroupNavigationEntry,
  rootGroupNavigationEntrySchema,
} from './groups'
import {
  type InternalLinkNavigationEntry,
  internalLinkNavigationEntrySchema,
  type LinkNavigationEntry,
  linkNavigationEntrySchema,
} from './link'
import {
  type InternalOpenApiNavigationEntry,
  internalOpenApiNavigationEntrySchema,
  type OpenApiNavigationEntry,
  openapiNavigationEntrySchema,
} from './openapi'
import {
  type InternalPageNavigationEntry,
  internalPageNavigationEntrySchema,
  type PageNavigationEntry,
  pageNavigationEntrySchema,
} from './pages'

/**
 * Example navigation definition.
 * All of the below items would be shown as top level links, tabs, or in the sidebar global section.
 *
 * This object is essentially a special instance of `group`
 */
const _exampleNavigation: NavigationConfig = {
  tabs: [
    {
      title: 'Home',
      path: '/',
    },
    {
      title: 'Element API',
      path: '/api',
      icon: 'earth',
    },
  ],
  header: [
    {
      type: 'link',
      title: 'Home',
      to: '/',
    },
    {
      type: 'group',
      title: 'APIs',
      children: [
        {
          type: 'link',
          title: 'Element API',
          to: '/earth-api',
        },
        {
          type: 'spacer',
        },
        {
          type: 'link',
          title: 'External link',
          to: 'https://scalar.com',
          newTab: true,
        },
      ],
    },
  ],
  sidebar: [
    {
      title: 'Home',
      path: '/',
      icon: 'home',
    },
    {
      title: 'APIs',
      children: [
        {
          title: 'Element API',
          path: '/earth-api',
        },
        {
          title: 'About',
          path: '/about',
        },
      ],
    },
  ],
  routes: {
    '/': {
      title: 'Home',
      type: 'group',
      children: {
        '/': {
          type: 'page',
          filepath: 'pages/index.md',
          title: 'Home',
          layout: {
            toc: false,
            header: false,
            sidebar: false,
            tabs: false,
          },
        },
        '/terms': './pages/terms.md',
        '/privacy': './pages/privacy.md',
      },
    },
    '/earth-api': {
      title: 'Element API',
      type: 'openapi',
      filepath: 'api/earth.yaml',
    },
    '/link': {
      type: 'link',
      url: 'https://www.google.com',
      title: 'Google',
    },
    '/about': {
      type: 'page',
      filepath: 'pages/about.md',
      title: 'About',
    },
  },
}

export type BaseNavigation = {
  title: string
  icon?: Icon
}

export const baseNavigationSchema = z.object({
  title: z.string(),
  icon: iconSchema.optional(),
}) satisfies z.ZodType<BaseNavigation>

export type NavigationExternalStyle = 'text' | 'button'

export type NavigationExternal = BaseNavigation & {
  url: string
  style?: NavigationExternalStyle
  newTab?: boolean
}

export const navigationExternalSchema = baseNavigationSchema.extend({
  url: z.url().describe('An external link, e.g. https://www.example.com'),
  style: z.enum(['text', 'button']).optional(),
  newTab: z.boolean().default(true),
}) satisfies z.ZodType<NavigationExternal>

export type NavigationPath = BaseNavigation & {
  path: string
}

export const navigationPathSchema = baseNavigationSchema.extend({
  path: pathSchema.describe('A route path to another docs page, e.g. /about'),
}) satisfies z.ZodType<NavigationPath>

export type NavigationSingleItem = NavigationPath | NavigationExternal

export const navigationSingleItemSchema: z.ZodType<NavigationSingleItem> =
  z.union([navigationPathSchema, navigationExternalSchema])

export type NavigationGroup = BaseNavigation & {
  children: NavigationSingleItem[]
}

/** Navigation links can also be grouped dropdowns */
export const navigationGroupSchema: z.ZodType<NavigationGroup> =
  baseNavigationSchema.extend({
    children: navigationSingleItemSchema.array(),
  })

export type NavigationItem = NavigationSingleItem | NavigationGroup

export const navigationItemSchema: z.ZodType<NavigationItem> = z.union([
  navigationSingleItemSchema,
  navigationGroupSchema,
])

// ---------------------------------------------------------------------------

export type BaseNavigationConfig = {
  title?: string
  tabs?: NavigationPath[]
  header?: HeaderItem[]
  sidebar?: NavigationItem[]
}

/**
 * The top level navigation definition schema. Any paths here will be with respect to the root
 * These items will show up in the tabs; the header links; or the sidebar dropdown depending on the configuration
 */
const baseNavigationConfigSchema = z.object({
  title: z.string().optional().describe('The title of the version.'),
  /** Top level links in the tabs. These links show on every page. */
  tabs: navigationPathSchema.array().optional(),
  /** Header links to different pages */
  header: headerItemSchema.array().optional(),
  /** Top level global links in the sidebar. These links show on every page with a sidebar. */
  sidebar: navigationItemSchema.array().optional(),
}) satisfies z.ZodType<BaseNavigationConfig>

export type NavigationRouteEntry =
  | PageNavigationEntry
  | LinkNavigationEntry
  | OpenApiNavigationEntry
  | RootGroupNavigationEntry

const navigationRouteEntrySchema = z.union([
  pageNavigationEntrySchema,
  linkNavigationEntrySchema,
  openapiNavigationEntrySchema,
  rootGroupNavigationEntrySchema,
]) satisfies z.ZodType<NavigationRouteEntry>

export type NavigationRoutes = Record<string, NavigationRouteEntry>

/** A record of paths to navigation entries. Each top level key represents a unique sidebar configuration.
 * Only the root sidebar will be show by default.
 * Other entries here require specifying a link to the entry in the header, tabs, or sidebar global links
 */
const navigationRoutesSchema = z.record(
  pathSchema,
  navigationRouteEntrySchema,
) satisfies z.ZodType<NavigationRoutes>

/**
 * The top level navigation definition schema. Any paths here will be with respect to the root
 * This is a slightly modified version of the group NavigationRecord where groups have a subset of properties
 */
export type NavigationConfig = BaseNavigationConfig & {
  routes: NavigationRoutes
}

/** A record of paths to navigation entries. Each top level key represents a unique sidebar configuration.
 * Only the root sidebar will be show by default.
 * Other entries here require specifying a link to the entry in the header, tabs, or sidebar global links
 */
export const navigationConfigSchema: z.ZodType<NavigationConfig> =
  baseNavigationConfigSchema.extend({
    routes: navigationRoutesSchema,
  })

export type InternalNavigationRouteEntry =
  | InternalPageNavigationEntry
  | InternalOpenApiNavigationEntry
  | InternalLinkNavigationEntry
  | InternalRootGroupNavigationEntry

const internalNavigationRouteEntrySchema = z.union([
  internalPageNavigationEntrySchema,
  internalOpenApiNavigationEntrySchema,
  internalLinkNavigationEntrySchema,
  internalRootGroupNavigationEntrySchema,
]) satisfies z.ZodType<InternalNavigationRouteEntry>

export type InternalNavigationRoutes = Record<
  string,
  InternalNavigationRouteEntry
>

const internalNavigationRoutesSchema = z.record(
  pathSchema,
  internalNavigationRouteEntrySchema,
) satisfies z.ZodType<InternalNavigationRoutes>

export type InternalNavigationConfig = BaseNavigationConfig & {
  uid: string
  name: string
  routes: InternalNavigationRoutes
}

/**
 * The top level navigation definition schema. Any paths here will be with respect to the root
 * This is a slightly modified version of the group NavigationRecord where groups have a subset of properties
 */
export const internalNavigationConfigSchema: z.ZodType<InternalNavigationConfig> =
  baseNavigationConfigSchema.extend({
    uid: z.string(),
    name: z.string(),
    routes: internalNavigationRoutesSchema,
  })
